import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { useCallback, useRef } from 'react';

/**
 * Transition Lenses专题页神策埋点Hook
 * 对应Vue版本中transition-lenses/TransitionLenses.vue的神策埋点功能
 */
export function useTransitionTracker() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  // 页面浏览状态跟踪
  const pageViewSended = useRef(false);
  const enterTimer = useRef(Date.now());

  /**
   * 神策埋点：专题页页面浏览
   * @TrackType sensorsdata
   * @EventType TransitionPage_View
   * @EventDesc 专题页页面浏览
   * @EventParams {
   *  refer_page_name: 前向页面名称,
   *  refer_url_path: 前向页面路径,
   * }
   */
  const trackPageView = useCallback(() => {
    try {
      if (pageViewSended.current) return;
      pageViewSended.current = true;

      // 重置开始时间
      enterTimer.current = Date.now();
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      trackEvent(
        {
          eventName: 'TransitionPage_View',
          properties: {
            refer_page_name,
            refer_url_path,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('TransitionPage_View tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 神策埋点：FAQ点击
   * @TrackType sensorsdata
   * @EventType TransitionPageFAQMk_Click
   * @EventDesc FAQ点击
   * @EventParams {
   *  pits_index: FAQ序号
   * }
   */
  const trackFAQClick = useCallback(
    (params: { pits_index: number }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageFAQMk_Click',
            properties: {
              pits_index: params.pits_index,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageFAQMk_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：镜片品牌颜色按钮点击
   * @TrackType sensorsdata
   * @EventType TransitionPageLensBrandMktColorBtn_Click
   * @EventDesc 资源位点击
   * @EventParams {
   *  group_id: 分组id,
   *  mkt_name: 版位名称,
   *  color_name: 颜色名称
   * }
   */
  const trackColorBtnClick = useCallback(
    (params: { group_id: string; mkt_name: string; color_name: string }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensBrandMktColorBtn_Click',
            properties: {
              group_id: params.group_id,
              mkt_name: params.mkt_name,
              color_name: params.color_name,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensBrandMktColorBtn_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：镜片品牌轮播翻页按钮点击
   * @TrackType sensorsdata
   * @EventType TransitionPageLensBrandMktFlipBtn_Click
   * @EventDesc 资源位点击
   * @EventParams {
   *  group_id: 分组id,
   *  mkt_name: 版位名称,
   *  btn_name: 按钮方向
   * }
   */
  const trackFlipBtnClick = useCallback(
    (params: { group_id: string; mkt_name: string; btn_name: 'left' | 'right' }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensBrandMktFlipBtn_Click',
            properties: {
              group_id: params.group_id,
              mkt_name: params.mkt_name,
              btn_name: params.btn_name,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensBrandMktFlipBtn_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：镜片品牌轮播图点击
   * @TrackType sensorsdata
   * @EventType TransitionPageLensBrandMktSwiper_Click
   * @EventDesc 资源位点击
   * @EventParams {
   *  group_id: 分组id,
   *  mkt_name: 版位名称,
   *  image_index: 图片索引
   * }
   */
  const trackSwiperClick = useCallback(
    (params: { group_id: string; mkt_name: string; image_index: number }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensBrandMktSwiper_Click',
            properties: {
              group_id: params.group_id,
              mkt_name: params.mkt_name,
              image_index: params.image_index,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensBrandMktSwiper_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：商品坑位曝光
   * @TrackType sensorsdata
   * @EventType TransitionPageLensBrandProductPits_Exp
   * @EventDesc 商品坑位曝光
   * @EventParams {
   *  pits_index: 坑位排序,
   *  group_id: 分组id,
   *  mkt_name: 版位名称,
   *  shop_sku: 销售商品sku,
   *  business_sku: 平台sku,
   *  product_sku: 商品sku
   * }
   */
  const trackProductExp = useCallback(
    (params: {
      pits_index: number;
      group_id: string;
      mkt_name: string;
      shop_sku: string;
      business_sku: string;
      product_sku: string;
    }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensBrandProductPits_Exp',
            properties: params,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensBrandProductPits_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：商品坑位点击
   * @TrackType sensorsdata
   * @EventType TransitionPageLensBrandProductPits_Click
   * @EventDesc 商品坑位点击
   * @EventParams {
   *  pits_index: 坑位排序,
   *  group_id: 分组id,
   *  mkt_name: 版位名称,
   *  shop_sku: 销售商品sku,
   *  business_sku: 平台sku,
   *  product_sku: 商品sku
   * }
   */
  const trackProductClick = useCallback(
    (params: {
      pits_index: number;
      group_id: string;
      mkt_name: string;
      shop_sku: string;
      business_sku: string;
      product_sku: string;
    }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensBrandProductPits_Click',
            properties: params,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensBrandProductPits_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：查看更多按钮点击
   * @TrackType sensorsdata
   * @EventType TransitionPageLensBrandMktViewMoreBtn_Click
   * @EventDesc 查看更多按钮点击
   * @EventParams {
   *  group_id: 分组id,
   *  mkt_name: 版位名称
   * }
   */
  const trackViewMoreClick = useCallback(
    (params: { group_id: string; mkt_name: string }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensBrandMktViewMoreBtn_Click',
            properties: params,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensBrandMktViewMoreBtn_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：镜片概览坑位曝光
   * @TrackType sensorsdata
   * @EventType TransitionPageLensOverviewPits_Exp
   * @EventDesc 镜片概览坑位曝光
   * @EventParams {
   *  pits_index: 坑位排序,
   *  group_id: 分组id
   * }
   */
  const trackOverviewExp = useCallback(
    (params: { pits_index: number; group_id: string }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensOverviewPits_Exp',
            properties: params,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensOverviewPits_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：镜片概览坑位点击
   * @TrackType sensorsdata
   * @EventType TransitionPageLensOverviewPits_Click
   * @EventDesc 镜片概览坑位点击
   * @EventParams {
   *  pits_index: 坑位排序,
   *  group_id: 分组id
   * }
   */
  const trackOverviewClick = useCallback(
    (params: { pits_index: number; group_id: string }) => {
      try {
        trackEvent(
          {
            eventName: 'TransitionPageLensOverviewPits_Click',
            properties: params,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('TransitionPageLensOverviewPits_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  return {
    trackPageView,
    trackFAQClick,
    trackColorBtnClick,
    trackFlipBtnClick,
    trackSwiperClick,
    trackProductExp,
    trackProductClick,
    trackViewMoreClick,
    trackOverviewExp,
    trackOverviewClick,
  };
}
